import React from "react";
import {
  Box,
  Button,
  Typography,
  IconButton,
  Paper,
  Stack,
  FormHelperText,
  alpha,
} from "@mui/material";
import { AddCircle, DeleteOutline, Inventory } from "@mui/icons-material";
import CustomInput from "../../../../../FormFields/CustomInput";
import MultiSelect from "../../../../../FormFields/CustomSelect/MultiSelect";

const MedicationInventorySection = ({
  formData = {},
  handleChange,
  individualMeds = [],
  handleIndMedChange,
  setIndividualMeds,
  selectedMedications = [],
  setSelectedMedications,
  medicineNames,
  isSearchingMedicines,
  errors = {},
  search,
  setSearch,
}) => {
  const isIndividual = formData.medicine_type === "Individual Medication";

  const normalizedOptions =
    medicineNames?.results?.map((m) => ({
      value: m.id ?? m.name,
      label: m.name,
    })) || [];

  const selectedMedicationOptions = normalizedOptions.filter((opt) =>
    selectedMedications.some(
      (sm) => String(sm.value) === String(opt.value)
    )
  );

  return (
    <Paper
      sx={{
        mt: 3,
        p: 2.5,
        bgcolor: "#f8fafc",
        border: "1.5px solid #e2e8f0",
        borderRadius: 2.5,
      }}
    >
      <Typography
        sx={{
          fontWeight: 700,
          color: "#0f172a",
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 2,
          fontSize: "0.9rem",
        }}
      >
        <Inventory sx={{ color: "#2469F0", fontSize: 20 }} />
        Inventory & Stock Management
      </Typography>

      {isIndividual ? (
        <Stack spacing={2}>
          {individualMeds.map((item, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                alignItems: "flex-start",
              }}
            >
              <Box sx={{ flex: "1 1 32%", minWidth: "220px" }}>
                <MultiSelect
                  options={normalizedOptions}
                  value={
                    item.name ? [{ value: item.name, label: item.name }] : []
                  }
                  onChange={(val) => {
                    const selected = val[0];
                    handleIndMedChange(
                      index,
                      "name",
                      selected?.label ?? selected?.value ?? ""
                    );
                  }}
                  onSearchChange={setSearch}
                  searchable
                  isLoading={isSearchingMedicines}
                  minChars={2}
                  getOptionLabel={(opt) => opt.label ?? opt.name}
                  getOptionValue={(opt) => opt.value ?? opt.id ?? opt.name}
                  placeholder="Search medicine..."
                  error={!!errors[`individual_meds[${index}].name`]}
                  helperText={errors[`individual_meds[${index}].name`]}
                />
              </Box>
              <Box sx={{ flex: "1 1 22%", minWidth: "140px" }}>
                <CustomInput
                  label="Dose"
                  value={item.dose}
                  onChange={(e) =>
                    handleIndMedChange(index, "dose", e.target.value)
                  }
                  error={!!errors[`individual_meds[${index}].dose`]}
                  placeholder="e.g., 10mg"
                  fullWidth
                />
              </Box>
              <Box sx={{ flex: "1 1 22%", minWidth: "140px" }}>
                <CustomInput
                  label="Stock Quantity"
                  type="number"
                  value={item.initial_stock_qty}
                  onChange={(e) =>
                    handleIndMedChange(
                      index,
                      "initial_stock_qty",
                      e.target.value
                    )
                  }
                  error={!!errors[`individual_meds[${index}].initial_stock_qty`]}
                  placeholder="0"
                  fullWidth
                />
              </Box>
              <Box
                sx={{
                  flex: "0 0 44px",
                  display: "flex",
                  alignItems: "center",
                  pt: 1,
                }}
              >
                {individualMeds.length > 1 && (
                  <IconButton
                    color="error"
                    onClick={() =>
                      setIndividualMeds(
                        individualMeds.filter((_, i) => i !== index)
                      )
                    }
                  >
                    <DeleteOutline />
                  </IconButton>
                )}
              </Box>
            </Box>
          ))}
          <Button
            startIcon={<AddCircle />}
            onClick={() =>
              setIndividualMeds([
                ...individualMeds,
                { name: "", dose: "", initial_stock_qty: "" },
              ])
            }
            sx={{
              alignSelf: "flex-start",
              color: "#2469F0",
              fontWeight: 600,
              textTransform: "none",
              "&:hover": { bgcolor: alpha("#2469F0", 0.08) },
            }}
          >
            Add Another Medicine
          </Button>
        </Stack>
      ) : (
        <Stack spacing={2}>
          <Box sx={{ width: "100%" }}>
            <MultiSelect
              label="Search Medicines"
              options={normalizedOptions}
              value={selectedMedications}
              onChange={(opt) => {
                const normalized = opt.map((o) => ({
                  value: o.value ?? o.id ?? o.label,
                  label: o.label ?? o.name,
                }));
                setSelectedMedications(normalized);
              }}
              onSearchChange={setSearch}
              searchable
              isLoading={isSearchingMedicines}
              minChars={2}
              getOptionLabel={(opt) => opt.label ?? opt.name}
              getOptionValue={(opt) => opt.value ?? opt.id ?? opt.label}
              placeholder="Type to search medicines..."
              error={!!errors.medicine_list}
              helperText={errors.medicine_list}
            />
          </Box>

          {errors.medicine_list && (
            <FormHelperText error>{errors.medicine_list}</FormHelperText>
          )}

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            <Box sx={{ flex: "1 1 22%", minWidth: "140px" }}>
              <CustomInput
                label="Number of Cards"
                name="number_of_cards"
                type="number"
                value={formData.number_of_cards}
                onChange={handleChange}
                placeholder="0"
                fullWidth
              />
            </Box>
            <Box sx={{ flex: "1 1 22%", minWidth: "140px" }}>
              <CustomInput
                label="Slots per Card"
                name="slots_per_card"
                type="number"
                value={formData.slots_per_card}
                onChange={handleChange}
                placeholder="0"
                fullWidth
              />
            </Box>
            <Box sx={{ flex: "1 1 22%", minWidth: "140px" }}>
              <CustomInput
                label="Loose Slots"
                name="loose_slots"
                type="number"
                value={formData.loose_slots}
                onChange={handleChange}
                placeholder="0"
                fullWidth
              />
            </Box>
            <Box sx={{ flex: "1 1 22%", minWidth: "140px" }}>
              <CustomInput
                label="Total Slots"
                type="number"
                value={formData.total_slots_remaining}
                disabled
                sx={{
                  width: "100%",
                  "& .MuiInputBase-root": {
                    bgcolor: "#dbeafe",
                    fontWeight: 700,
                    color: "#1e40af",
                  },
                }}
              />
            </Box>
          </Box>
        </Stack>
      )}
    </Paper>
  );
};

export default MedicationInventorySection;